import * as Sentry from '@sentry/remix';

import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/vi';
import 'dayjs/locale/zh';

import { EXPERIMENT_CONFIG } from './modules/dev-mode/components/CartExperimentDemo/constants';
import { COOKIE_MAX_AGE_30_DAYS, TIMEZONE_COOKIE_NAME } from './modules/shopify/constants';
import { RemixBrowser, useLocation, useMatches } from '@remix-run/react';
import { ExperimentProvider } from '@gem-sdk/experiment-react';
import { startTransition, StrictMode, useEffect } from 'react';
import { getInitialNamespaces } from 'remix-i18next/client';
import { getConfig } from './config/environment';
import { initReactI18next } from 'react-i18next';
import { hydrateRoot } from 'react-dom/client';
import { setTz, dayjs } from './utils/dayjs';
import { setCookie } from './utils';

import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import i18nConfig from './i18n';
import i18next from 'i18next';

const supportedLngs = ['en', 'vi', 'es', 'cn'];

Sentry.init({
  dsn: getConfig('SENTRY')?.DNS || '',
  tracesSampleRate: Number(getConfig('SENTRY')?.TRACES_SAMPLE_RATE) || 0.1,
  environment: getConfig('SENTRY')?.ENVIRONMENT || 'development',
  integrations: [
    Sentry.browserTracingIntegration({
      useEffect,
      useLocation,
      useMatches,
    }),
  ],
});

i18next.on('timezoneChanged', (tz) => {
  // TODO: Should create a instance to store timezone like i18next instance help hydrate client when timezone changed
  if (!tz) return;

  setCookie(TIMEZONE_COOKIE_NAME, tz, {
    maxAge: COOKIE_MAX_AGE_30_DAYS,
  });
  setTz(tz);
  i18next.changeLanguage(i18next.language);
});

i18next.on('languageChanged', (lng) => {
  const locale = lng === 'cn' ? 'zh' : supportedLngs.includes(lng) ? lng : 'en';
  dayjs.locale(locale);
});

const hydrate = () => {
  i18next
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(Backend)
    .init({
      ...i18nConfig,
      backend: { loadPath: '/locales/{{lng}}.json' },
      ns: getInitialNamespaces(),
      detection: {
        order: ['querystring', 'localStorage', 'navigator'],
        caches: ['localStorage'],
        lookupQuerystring: 'locale',
      },
      react: {
        useSuspense: false,
      },
      interpolation: {
        escapeValue: false,
      },
    })
    .then(() => {
      startTransition(() => {
        hydrateRoot(
          document.getElementById('root')!,
          <Sentry.ErrorBoundary fallback={<div>An error occurred</div>}>
            <StrictMode>
              <ExperimentProvider config={EXPERIMENT_CONFIG}>
                <RemixBrowser />
              </ExperimentProvider>
            </StrictMode>
          </Sentry.ErrorBoundary>,
        );
      });
    })
    .catch((error) => {
      Sentry.captureException(error);
      // Fallback hydration
      startTransition(() => {
        hydrateRoot(
          document.getElementById('root')!,
          <Sentry.ErrorBoundary fallback={<div>An error occurred</div>}>
            <StrictMode>
              <RemixBrowser />
            </StrictMode>
          </Sentry.ErrorBoundary>,
        );
      });
    });
};

// Schedule hydration
// In local/development, hydrate immediately to avoid requestIdleCallback
// being throttled in embedded iframes (Shopify Admin) until user interaction

const isInIframe = window.self !== window.top;
const handleHydrate = () => {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(hydrate);
  } else {
    setTimeout(hydrate, 1);
  }
};

if (isInIframe) {
  handleHydrate();
} else {
  startTransition(hydrate);
}
